import { z } from "zod/v4";
import { ChessPiecesSchema } from "../../../models/base-model";

const MoveAnnotationBaseSchema = z.object({});

export const AnnotationSeveritySchema = z.enum([
    "error", // Blunder, bad trade, hanging piece
    "warning", // Inaccuracy, missed tactic
    "info", // Neutral, e.g. "piece is attacked"
    "success", // Best move, good trade, nice tactic
]);
export type AnnotationSeverity = z.infer<typeof AnnotationSeveritySchema>;

/**
 * Annotation for when the player to move has left a piece undefended.
 * Used to mark a piece that is not protected by any other piece.
 */
const UndefendedPieceAnnotationSchema = MoveAnnotationBaseSchema.extend({
    type: z.literal("undefended-piece"),
    piece: ChessPiecesSchema,
    square: z.string(),
    isFirstTime: z.boolean(),
});

const ForkTargetSchema = z.object({
    piece: ChessPiecesSchema,
    square: z.string(),
    defenders: z.array(ChessPiecesSchema),
    attackers: z.array(ChessPiecesSchema),
});

export type ForkTarget = z.infer<typeof ForkTargetSchema>;

const ForkAnnotationSchema = MoveAnnotationBaseSchema.extend({
    type: z.literal("fork"),
    attacker: ChessPiecesSchema,
    squareDefenders: z.array(ChessPiecesSchema),
    squareAttackers: z.array(ChessPiecesSchema),
    square: z.string(),
    targets: z.array(ForkTargetSchema),
    canBeCaptured: z.boolean(),
    isCheck: z.boolean(),
    rating: z.number(),
    capture: ChessPiecesSchema.optional(),
    netGain: z.number(),
});

// TODO: implement
/**
 * Annotation for when the player to move has left a piece underprotected, meaning it is defended by fewer pieces than it is attacked by.
 * Only used when a piece is attacked by multiple pieces and defended by at least one piece, but the number of attackers exceeds the number of defenders.
 * When 0 pieces are defending it, it is considered undefended instead.
 */
const UnderprotectedPieceAnnotationSchema = MoveAnnotationBaseSchema.extend({
    type: z.literal("underprotected-piece"),
    piece: ChessPiecesSchema,
    square: z.string(),
    protectionCount: z.number(),
    attackCount: z.number(),
    isFirstTime: z.boolean(),
});

// TODO: implement
/**
 * Annotation for when the player to move has blundered a checkmate, allowing the opponent to deliver checkmate on their next move.
 */
const BlunderedCheckmateAnnotationSchema = MoveAnnotationBaseSchema.extend({
    type: z.literal("blundered-checkmate"),
});

// TODO: implement
/**
 * Annotation for when the player to move has made a bad trade, losing material in an exchange.
 * Can be longer trades involving multiple pieces, not just single piece captures.
 */
const BadTradeAnnotationSchema = MoveAnnotationBaseSchema.extend({
    type: z.literal("bad-trade"),
    pointsLoss: z.number(),
    tradeDepth: z.number(),
});

/**
 * Annotation for when a player sacrifices material (piece or pawn) for tactical/positional compensation.
 * Detects both immediate sacrifices (captures with material loss) and delayed sacrifices (leaving pieces hanging).
 * Quality tracked via severity: success = good sacrifice (compensated), error = bad sacrifice (uncompensated).
 */
const SacrificeAnnotationSchema = MoveAnnotationBaseSchema.extend({
    type: z.literal("sacrifice"),
    sacrificedPiece: ChessPiecesSchema,
    sacrificedSquare: z.string(),
    capturedPiece: ChessPiecesSchema.optional(), // what we captured (undefined for delayed sacrifices)
    isGambit: z.boolean(),
    materialLost: z.number(),
    evalImprovement: z.number(), // eval_after - eval_before (can be negative)
});

export const MoveAnnotationSchema = z.discriminatedUnion("type", [
    UndefendedPieceAnnotationSchema,
    ForkAnnotationSchema,
    SacrificeAnnotationSchema,
    // BlunderedCheckmateAnnotationSchema,
    // UnderprotectedPieceAnnotationSchema,
    // BadTradeAnnotationSchema,
]);

export type MoveAnnotation = z.infer<typeof MoveAnnotationSchema>;

export type MoveAnnotationType = MoveAnnotation["type"];

const from = (annotation: MoveAnnotation) => annotation;

export const MoveAnnotation = { from };
